import { useEffect, useState } from "react";
import { Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { DOMAINS } from "@/lib/soap";

const blank = { name: "", dob: "", diagnosis: "", guardian: "", notes: "", goals: [], behaviors: [] };

function ListEditor({ title, items, onChange, fields, testPrefix }) {
  const upd = (i, patch) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{title}</Label>
        <Button type="button" size="sm" variant="ghost" onClick={() => onChange([...items, Object.fromEntries(fields.map((f) => [f.key, f.default || ""]))])} data-testid={`${testPrefix}-add-button`}>
          <Plus className="h-4 w-4 mr-1" />Add
        </Button>
      </div>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 items-start" data-testid={`${testPrefix}-row-${i}`}>
          {fields.map((f) =>
            f.options ? (
              <Select key={f.key} value={it[f.key] || f.default} onValueChange={(v) => upd(i, { [f.key]: v })}>
                <SelectTrigger className="h-9 w-40 shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <Input key={f.key} value={it[f.key] || ""} onChange={(e) => upd(i, { [f.key]: e.target.value })} placeholder={f.placeholder} className={`h-9 ${f.grow ? "flex-1" : "w-44"}`} data-testid={`${testPrefix}-${f.key}-input-${i}`} />
            )
          )}
          <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => onChange(items.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
        </div>
      ))}
    </div>
  );
}

export function ClientDialog({ open, onOpenChange, client, onSaved }) {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(client ? { ...blank, ...client } : blank); }, [open, client]);

  const save = async () => {
    if (!f.name.trim()) return toast.error("Client name is required");
    setBusy(true);
    try {
      const payload = { ...f, goals: f.goals.filter((g) => g.name.trim()), behaviors: f.behaviors.filter((b) => b.name.trim()) };
      const { data } = client ? await api.put(`/clients/${client.id}`, payload) : await api.post("/clients", payload);
      onSaved(data);
      onOpenChange(false);
      toast.success("Client saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const field = (k, label, props = {}) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`client-${k}-input`} {...props} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-dialog">
        <DialogHeader><DialogTitle>{client ? "Edit client" : "New client"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          {field("name", "Client name / initials")}
          {field("dob", "Date of birth", { type: "date" })}
          {field("diagnosis", "Diagnosis")}
          {field("guardian", "Caregiver / guardian")}
        </div>
        <ListEditor
          title="Treatment goals / targets"
          items={f.goals}
          onChange={(goals) => setF({ ...f, goals })}
          testPrefix="client-goal"
          fields={[{ key: "name", placeholder: "Goal (e.g. Mand for items)", grow: true }, { key: "domain", options: DOMAINS, default: "Communication" }]}
        />
        <ListEditor
          title="Behaviors to track"
          items={f.behaviors}
          onChange={(behaviors) => setF({ ...f, behaviors })}
          testPrefix="client-behavior"
          fields={[{ key: "name", placeholder: "Behavior" }, { key: "definition", placeholder: "Operational definition", grow: true }]}
        />
        <div className="space-y-1.5">
          <Label>Notes</Label>
          <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Reinforcers, preferences, safety notes…" data-testid="client-notes-input" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-full px-6" data-testid="client-save-button">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save client
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
